import {Button, Input, Radio} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useMemo, useState} from 'react'
import {compose, mapProps, nest, pure} from 'recompose'
import {ErrorSummaries} from 'views/AppLocals/factory/createEditableForm'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {convertFromString, convertToString} from 'views/SlateEditor/functions'
import {ModalEditorToolbar} from 'views/SlateEditor/SlateEditor'
import FormActionBar from '../../../../components/form/FormActionBar'
import SlateEditor from '../../../SlateEditor'
import {createControlledFormField} from '../../factory/createFormField'
import TextArea from "antd/lib/input/TextArea";
import {CustomToggleField} from "../../../../components/form/fields/ToggleField";
import Pure from "../../../../components/Pure";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {BsShieldFillExclamation} from "react-icons/bs";
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {ROOT_URL} from "../../../../envs/_current/config";
import TooltipApp from "../../../../components/TooltipApp";
import {StatusOptions} from "../../CatholicHousehold/consts/StatusOptions";
import {useProductVariantForm} from "../functions/useProductVariantForm";
import classNames from "classnames";
import ForDevelop from "../../../../envs/ForDevelop";

export const formSchema = {
    status: {
        title: 'status',
        children: [
            {
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="custom-radio-button"
                        />
                    )
                },
            },
        ],
    },
    title: {
        title: 'product name',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'Enter product/ Service name',
                    showCount: true,
                    maxLength: 120,
                    className: 'rounded-md',
                }),
            },
        ],
    },
    description: {
        title: 'short description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 5,
                    showCount: true,
                    maxLength: 500,
                    placeholder: 'short description',
                    Component: TextArea,
                    className: 'scrollBarThin',
                }),
            },
        ],
    },
    idname: {
        title: 'url',
        children: {
            name: 'idname',
            component: compose(
                mapProps(({onChange, value}) => ({
                    onChange,
                    value,
                    name: 'idname',
                    addonBefore: 'https://.../course/',
                    placeholder: 'please input name!',
                    type: 'text',
                    trigger: ['focus'],
                    title: ROOT_URL + '/course/' + value,
                    placement: 'topLeft',
                })),
                pure
            )(nest(TooltipApp, Input)),
        },
    },
    product_type: {
        title: 'product types',
        children: [
            {
                name: 'product_type',
                component: createControlledFormField({
                    placeholder: 'enter product type',
                    showCount: true,
                    maxLength: 120,
                    className: 'rounded-md',
                }),
            },
        ],
    },
    author_name: {
        title: 'author',
        children: [
            {
                name: 'author_name',
                component: createControlledFormField({
                    placeholder: 'enter author name',
                    showCount: true,
                    maxLength: 120,
                    className: 'rounded-md',
                }),
            },
        ],
    },
    privacy: {
        title: 'privacy',
        children: {
            name: 'privacy',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 2,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 2 : 1))
                    },
                    title: translate('privacy'),
                    description: translate("This parameter allows whether non-member users are restricted from access or not."),
                    className: "my-3 p-2 border border-dashed border-primary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },
    about: {
        title: 'product details',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    className: 'background border rounded-lg',
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },

}

export const ProductFormCreate = ({
                                      owner_id,
                                      owner_type,
                                      onCancel = NullAsDefault,
                                      onSubmit = NullAsDefault,
                                  }) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}

        const {title} = values ?? {}

        if (!title) {
            errors.title = t('required field')
        }

        return errors
    }

    const initialValues = useMemo(() => {
        return {
            owner_id,
            owner_type,
            title: undefined,
            description: undefined,
        }
    }, [owner_id, owner_type])

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    ...rest
                } = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                <ActionModalWrapper
                    action={
                        <FormActionGroups
                            submitProps={{
                                label: 'create',
                            }}
                        />
                    }>
                    <div className="flex flex-col gap-3">
                        <Pure>
                            <FieldsFactory
                                formSchema={[
                                    formSchema.title,
                                    formSchema.description,
                                    formSchema.product_type,
                                    formSchema.author_name,
                                    formSchema.about,
                                ]}
                            />
                        </Pure>

                    </div>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const ProductFormEdit = ({
                                    owner_id,
                                    owner_type,
                                    onCancel = NullAsDefault,
                                    onSubmit = NullAsDefault,
                                    initialValues = {},
                                }) => {
    const t = useTranslate()
    const [current, setCurrent] = useState('overview')
    const {
        product_variants = [],
        about_html,
    } = initialValues ?? {}

    const _initialValues = useMemo(() => {
        const {
            about,
            ...rest
        } = initialValues ?? {}
        const status_options = [
            StatusOptions.normal,
            StatusOptions.closed,
        ].map(deepTranslate(t))
        return {
            ...rest,
            owner_id,
            owner_type,
            status_options,
            about: convertFromString(about),
        }
    }, [initialValues, owner_id, owner_type])

    const validate = (values) => {
        let errors = {}
        const {title, time_open} = values ?? {}
        if (!title) {
            errors.title = t('required field')
        }
        return errors
    }

    const {
        modal: variantModal,
        handleAction: handleVariantAction,
        onCancel: onVariantCancel,
        refreshKey: refreshVariantKey,
        addVariant,
        editVariantInfo,
        deleteVariant
    } = useProductVariantForm({product: initialValues})

    const VariantItem = ({label, value, className = 'text-color-100'}) => {
        return value && (
            <div className="flex flex-col text-xs">
                {label && (
                    <span className="text-color-300 tracking-wider">
                        {t(label)}
                    </span>
                )}
                <span className={classNames("font-semibold tracking-wide", className)}>
                    {value || ' - '}
                </span>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <Formik
                validate={validate}
                validateOnMount={true}
                validateOnChange={true}
                enableReinitialize={true}
                initialValues={_initialValues}
                onSubmit={(values) => {
                    const changes = shallowDiff(values, _initialValues)
                    const {
                        about,
                        resetKey,
                        owner_id,
                        owner_type,
                        status_options,
                        ...rest
                    } = changes ?? {}
                    onSubmit(
                        _.omitBy(
                            {
                                ...rest,
                                about: about ? convertToString(about) : undefined,
                            },
                            _.isUndefined
                        )
                    )
                }}>
                <React.Fragment>
                    <ErrorSummaries/>
                    <ActionModalWrapper
                        tab={current}
                        action={<FormActionBar submitText="save"/>}
                        onTabChange={(key) => {
                            setCurrent(key)
                        }}
                        tabs={[
                            {
                                key: 'overview',
                                label: 'overview',
                                component: ({child}) => child,
                            },
                            {
                                key: 'about',
                                label: 'about',
                                component: () => (
                                    <div className="flex flex-col gap-6">
                                        <FieldsFactory formSchema={[formSchema.about]}/>
                                        <ForDevelop>
                                            <div className="p-3 border border-color-50 rounded-xl flex flex-col gap-2">
                                                <label className="text-sm font-medium text-color-200 tracking-wider">
                                                    {('Dữ liệu gốc ???')}
                                                </label>
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: about_html }}
                                                    className="prose ml-5 text-color-300 text-xs text-color-300 tracking-wider"
                                                />
                                            </div>
                                        </ForDevelop>
                                    </div>
                                ),
                            },
                            {
                                key: 'variant',
                                label: 'variants',
                                component: () => (
                                    <Pure>
                                        <div className="flex flex-col gap-6">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 font-semibold text-sm text-color-200 tracking-wider">
                                                    {t('product variants list')}
                                                </div>
                                                <Button
                                                    size="small"
                                                    color="primary" variant="filled"
                                                    onClick={() => {handleVariantAction("VARIANT_CREATE")}}
                                                    className="rounded-md font-medium text-xs">
                                                    {t('create variant')}
                                                </Button>
                                            </div>
                                            <div className="flex flex-col gap-4">
                                                {product_variants?.map((e, i) => {
                                                    const {
                                                        active, weight, barcode, variant_code, compare_price, price, quantity, sold_quantity, blocked_quantity,
                                                        attribute1_name, attribute1_value, attribute2_name, attribute2_value,
                                                    } = e ?? {}

                                                    const itemDetails = [
                                                        { label: "barcode", value: barcode },
                                                        { label: "variant code", value: variant_code },
                                                        { label: "status", value: active ? t('active') : t('locked') },
                                                        { label: attribute1_name, value: attribute1_value },
                                                        { label: attribute2_name, value: attribute2_value },
                                                        { label: "weight", value: `${weight} g` },
                                                        { label: "quantity", value: quantity },
                                                        { label: "sold quantity", value: sold_quantity },
                                                        { label: "blocked quantity", value: blocked_quantity },
                                                        { label: "compare price", value: compare_price.toLocaleString() , className: 'text-color-000'},
                                                        { label: "price", value: price.toLocaleString(), className: 'text-primary'},
                                                    ];

                                                    return (
                                                        <div key={i} className="px-3 py-2 border border-color-50 hover:shadow rounded-md hover:scale-2xs cursor-pointer">
                                                            <div className="mb-2 pb-2 border-b border-color-50 flex items-center gap-2">
                                                                <span className="flex-1 font-bold text-indigo-600 tracking-wide">
                                                                    {t('variants')} {i + 1}
                                                                </span>
                                                                <div className="flex items-center gap-x-3">
                                                                    <div
                                                                        onClick={() => {handleVariantAction("VARIANT_UPDATE", e)}}
                                                                        className="font-medium text-xs text-blue-600 hover:text-blue-700 cursor-pointer">
                                                                        {t('edit')}
                                                                    </div>
                                                                    <div
                                                                        onClick={() => {handleVariantAction("VARIANT_DELETE", e)}}
                                                                        className="font-medium text-xs text-primary-600 hover:text-primary-700 cursor-pointer">
                                                                        {t('delete')}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="grid grid-cols-3 gap-2">
                                                                {itemDetails?.map((ee, ii) => (
                                                                    <VariantItem key={ii} label={ee?.label} value={ee?.value} className={ee?.className}/>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </Pure>
                                ),
                            },
                        ]}>
                        <Pure>
                            <FieldsFactory
                                formSchema={[
                                    formSchema.title,
                                    formSchema.idname,
                                    formSchema.description,
                                    formSchema.product_type,
                                    formSchema.author_name,
                                ]}
                            />
                        </Pure>
                        <Pure>
                            <div className="flex flex-col gap-3">
                                <HeaderWithIconLine
                                    className="pt-2 mt-3"
                                    renderTitle={() => (
                                        <div className="flex items-center gap-2">
                                            <BsShieldFillExclamation size={16} className="text-color-500"/>
                                            <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                                                {t('privacy')}
                                            </span>
                                        </div>
                                    )}
                                />
                                <div className="ml-2 flex flex-col gap-3">
                                    <FieldsFactory
                                        formSchema={[
                                            formSchema.status,
                                            formSchema.privacy,
                                        ]}
                                    />
                                </div>
                            </div>
                        </Pure>
                    </ActionModalWrapper>
                </React.Fragment>
            </Formik>
            {variantModal}
        </div>
    )
}
